import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import { extractVideoId } from '../services/youtubeService';

/**
 * VideoCard component for displaying full-length videos with 16:9 aspect ratio
 * @param {Object} props - Component props
 * @param {Object} props.video - Video data object
 * @param {boolean} props.isLive - Whether this is a live video
 * @param {boolean} props.isMobileGrid - Whether in mobile grid layout
 * @returns {JSX.Element}
 */
const VideoCard = ({ video, isLive = false, isMobileGrid = false }) => {
  const navigate = useNavigate();
  const { theme } = useConfig();
  const [isHovering, setIsHovering] = useState(false);
  
  // Get video ID - use the youtube_video_id or id field directly
  const videoId = video?.youtube_video_id || video?.id || extractVideoId(video?.url || video?.videoUrl);
  
  // Format duration (convert seconds to MM:SS or HH:MM:SS)
  const formatDuration = (duration) => {
    if (!duration) return '';
    
    // If it's already formatted (contains ":"), return as-is
    if (typeof duration === 'string' && duration.includes(':')) {
      return duration;
    }
    
    // Convert to number and format
    const seconds = Number(duration);
    if (isNaN(seconds)) return '';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };
  
  // Format view count
  const formatViews = (views) => {
    if (!views) return '0 views';
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M views`;
    }
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K views`;
    }
    return `${views} views`;
  };
  
  // Handle click navigation
  const handleClick = () => {
    if (video?.id) {
      navigate(`/videos/${video.id}`);
    } else if (videoId) {
      window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank');
    }
  };
  
  return (
    <div 
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        cursor: 'pointer',
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'white',
        boxShadow: isHovering 
          ? '0 8px 24px rgba(0, 0, 0, 0.12)'
          : '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        transform: isHovering ? 'translateY(-2px)' : 'translateY(0)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Thumbnail Container - 16:9 Aspect Ratio */}
      <div style={{
        position: 'relative',
        width: '100%',
        paddingBottom: '56.25%', // 16:9 aspect ratio
        backgroundColor: '#000',
        overflow: 'hidden'
      }}>
        {/* Video Thumbnail - Use provided thumbnail or generate from video ID */}
        {video?.thumbnail ? (
          <img 
            src={video.thumbnail}
            alt={video?.title || 'Video thumbnail'}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : videoId ? (
          <img 
            src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
            alt={video?.title || 'Video thumbnail'}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
              <path d="M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
            </svg>
          </div>
        )}
        
        {/* Duration Badge */}
        {video?.duration && !isLive && (
          <div style={{
            position: 'absolute',
            bottom: '8px',
            right: '8px',
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {formatDuration(video.duration)}
          </div>
        )}
        
        {/* LIVE Badge */}
        {isLive && (
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            background: '#ff0000',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              background: 'white',
              borderRadius: '50%',
              animation: 'pulse 1.5s infinite'
            }} />
            LIVE
          </div>
        )}
        
        {/* Hover Play Overlay */}
        {isHovering && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '50%',
            width: '64px',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        )}
        
        {/* Title Overlay for Mobile Grid */}
        {isMobileGrid && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)',
            padding: '8px 6px 6px',
            zIndex: 1
          }}>
            <h4 style={{
              margin: 0,
              fontSize: '12px',
              fontWeight: '600',
              color: 'white',
              lineHeight: '1.3',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textShadow: '0 1px 3px rgba(0,0,0,0.8)'
            }}>
              {video?.title || 'Untitled Video'}
            </h4>
          </div>
        )}
      </div>
      
      {/* Content Section - Only show on desktop or non-grid mobile */}
      {!isMobileGrid && (
        <div style={{
          padding: '8px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '3px'
        }}>
          {/* Title */}
          <h4 style={{
            margin: 0,
            fontSize: '15px',
            fontWeight: '600',
            color: theme.isDarkBackground ? '#333333' : theme.colors.text,
            lineHeight: '1.4',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {video?.title || 'Untitled Video'}
          </h4>
          
          {/* Speaker & Views */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            color: theme.colors.darkGray || '#666'
          }}>
            {video?.speakerName && (
              <>
                <span>{video.speakerName}</span>
                <span>•</span>
              </>
            )}
            <span>{formatViews(video?.view_count || video?.viewCount || 0)}</span>
          </div>
          
          {/* Published Date */}
          {(video?.published || video?.published_at || video?.publishedAt) && (
            <div style={{
              fontSize: '12px',
              color: theme.colors.mediumGray || '#999'
            }}>
              {video.published || 
               (video.published_at && new Date(video.published_at).toLocaleDateString()) || 
               (video.publishedAt && new Date(video.publishedAt).toLocaleDateString())}
            </div>
          )}
          
          {/* Event/Series Label */}
          {video?.eventName && video.eventName.toLowerCase() !== 'all' && (
            <div style={{
              marginTop: '2px',
              fontSize: '11px',
              color: theme.colors.linkColor || theme.colors.primary,
              fontWeight: '500'
            }}>
              {video.eventName}
            </div>
          )}
        </div>
      )}
      
      {/* CSS Animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default VideoCard;